import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { GenericSortPaginationRequest } from '../../interfaces/services';
import { LanesResponse } from '../../interfaces/services/lanes';
import { ShippersResponse } from '../../interfaces/services/shippers';
import { ApiStatus } from '../../utils/enums';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { formatHealthFilters } from '../../utils/stateHelpers';

interface ShipperHealthState {
    networkShipperHealthApiStatus: ApiStatus;
    networkShipperHealthApiParams: GenericSortPaginationRequest;
    networkShipperHealth: ShippersResponse;
    shipperHealthDetailsApiStatus: ApiStatus;
    shipperHealthDetails: ShippersResponse;
    shipperHealthLanesApiStatus: ApiStatus;
    shipperHealthLanesApiParams: GenericSortPaginationRequest;
    shipperHealthLanes: LanesResponse;
}

// TODO: TCSC-52750
// interface ShipperHealthLanesRequest extends GenericSortPaginationRequest {
interface ShipperHealthLanesRequest {
    shipperId: string;
}

const initialState: ShipperHealthState = {
    networkShipperHealthApiStatus: ApiStatus.Idle,
    networkShipperHealthApiParams: {
        pageNumber: 1,
        pageSize: 100,
        sortBy: [
            { field: 'pickup_party_health_grade', sort: 'asc' },
            { field: 'freight_order_count_rank', sort: 'desc' },
        ],
    },
    networkShipperHealth: {
        pickup_party_count: 0,
        pickup_party_health: {
            load_hours_duration_grade: null,
            netwise_in_transit_delay_hours_ratio_grade: null,
            scheduled_vs_actual_miles_per_hour_grade: null,
        },
        pickup_parties: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
    shipperHealthDetailsApiStatus: ApiStatus.Idle,
    shipperHealthDetails: {
        pickup_party_count: 0,
        pickup_party_health: {
            load_hours_duration_grade: null,
            netwise_in_transit_delay_hours_ratio_grade: null,
            scheduled_vs_actual_miles_per_hour_grade: null,
        },
        pickup_parties: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
    shipperHealthLanesApiStatus: ApiStatus.Idle,
    shipperHealthLanesApiParams: {
        pageNumber: 1,
        pageSize: 100,
        sortBy: [
            { field: 'freight_order_count', sort: 'desc' },
            { field: 'load_hours_duration', sort: 'desc' },
        ],
    },
    shipperHealthLanes: {
        lane_count: 0,
        lanes: [],
        page_number: 0,
        size: 0,
        pages: 0,
    },
};

// TODO: TCSC-52750
export const fetchNetworkShipperHealth = createAsyncThunk(
    'shipperHealth/fetchNetworkShipperHealth',
    async () => {
        try {
            // const sortByList = sortBy.map((sortByObject) => {
            //     return `${sortByObject.sort === 'asc' ? '+' : '-'}${sortByObject.field}`;
            // });

            // const urlParams = new URLSearchParams();
            // if (sortByList.length > 0) {
            //     urlParams.append('sort_by', sortByList.toString());
            // }
            // urlParams.append('page_number', pageNumber.toString());
            // urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                // url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties?${urlParams}`,
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties`,
                body: {
                    filter_criteria: formatHealthFilters(),
                },
            }) as AxiosResponse<ShippersResponse>;

            return data;
        } catch (error) {
            console.error('Unable to fetch network shipper health', error);
            toast.error('Unable to fetch network shipper health');
            throw error;
        }
    }
);

export const fetchShipperHealthDetails = createAsyncThunk(
    'shipperHealth/fetchShipperHealthDetails',
    async (
        {
            shipperId,
        }: {
            shipperId: string;
        },
        thunkApi
    ) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/pickup-parties`,
                body: {
                    filter_criteria: {
                        ...formatHealthFilters(),
                        pickup_party_ids: [shipperId],
                    },
                },
            }) as AxiosResponse<ShippersResponse>;

            if (data.pickup_parties.length === 0) {
                return thunkApi.rejectWithValue(data);
            }

            return data;
        } catch (error) {
            console.error('Unable to fetch shipper health details', error);
            toast.error('Unable to fetch shipper health details');
            throw error;
        }
    }
);

// TODO: TCSC-52750
export const fetchShipperHealthLanes = createAsyncThunk(
    'shipperHealth/fetchShipperHealthLanes',
    async ({
        shipperId,
        // pageNumber,
        // pageSize,
        // sortBy,
    }: ShipperHealthLanesRequest) => {
        try {
            // const sortByList = sortBy.map((sortByObject) => {
            //     return `${sortByObject.sort === 'asc' ? '+' : '-'}${sortByObject.field}`;
            // });

            // const urlParams = new URLSearchParams();
            // if (sortByList.length > 0) {
            //     urlParams.append('sort_by', sortByList.toString());
            // }
            // urlParams.append('page_number', pageNumber.toString());
            // urlParams.append('size', pageSize.toString());

            const { data } = await ApiService.post({
                // url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes?${urlParams}`,
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/lanes`,
                body: {
                    filter_criteria: {
                        ...formatHealthFilters(),
                        pickup_party_ids: [shipperId],
                    },
                },
            }) as AxiosResponse<LanesResponse>;

            return data;
        } catch (error) {
            console.error('Unable to fetch shipper lanes', error);
            toast.error('Unable to fetch shipper lanes');
            throw error;
        }
    }
);

export const shipperHealthSlice = createSlice({
    name: 'shipperHealth',
    initialState,
    reducers: {
        updateNetworkShipperHealthApiParams: (state, action: PayloadAction<GenericSortPaginationRequest>) => {
            state.networkShipperHealthApiParams = action.payload;
        },
        updateShipperHealthLanesApiParams: (state, action: PayloadAction<GenericSortPaginationRequest>) => {
            state.shipperHealthLanesApiParams = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchNetworkShipperHealth.pending, (state) => {
            state.networkShipperHealthApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchNetworkShipperHealth.fulfilled, (state, action) => {
            state.networkShipperHealthApiStatus = ApiStatus.Success;
            state.networkShipperHealth = action.payload;
        });
        builder.addCase(fetchNetworkShipperHealth.rejected, (state) => {
            state.networkShipperHealthApiStatus = ApiStatus.Failure;
            state.networkShipperHealth = initialState.networkShipperHealth;
        });

        builder.addCase(fetchShipperHealthDetails.pending, (state) => {
            state.shipperHealthDetailsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchShipperHealthDetails.fulfilled, (state, action) => {
            state.shipperHealthDetailsApiStatus = ApiStatus.Success;
            state.shipperHealthDetails = action.payload;
        });
        builder.addCase(fetchShipperHealthDetails.rejected, (state) => {
            state.shipperHealthDetailsApiStatus = ApiStatus.Failure;
            state.shipperHealthDetails = initialState.shipperHealthDetails;
        });

        builder.addCase(fetchShipperHealthLanes.pending, (state) => {
            state.shipperHealthLanesApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchShipperHealthLanes.fulfilled, (state, action) => {
            state.shipperHealthLanesApiStatus = ApiStatus.Success;
            state.shipperHealthLanes = action.payload;
        });
        builder.addCase(fetchShipperHealthLanes.rejected, (state) => {
            state.shipperHealthLanesApiStatus = ApiStatus.Failure;
            state.shipperHealthLanes = initialState.shipperHealthLanes;
        });
    },
});

export const {
    updateNetworkShipperHealthApiParams,
    updateShipperHealthLanesApiParams,
} = shipperHealthSlice.actions;
export default shipperHealthSlice.reducer;
